import { yupResolver } from '@hookform/resolvers/yup'
import { DateOnly } from '@if/core/date'
import { FormBody, FormContainerProvider, FormSetup } from '@if/mui'
import TuneIcon from '@mui/icons-material/Tune'
import { Card, CardContent, CardHeader, Divider, Grid, Stack, Typography } from '@mui/material'
import PageSpinner from '@src/components/PageSpinner'
import { useUser } from '@src/contexts/AppContext'
import { useCurrentCustomBuydownPlan, useSaveCustomBuydownPlan } from '@src/data/api/custom-buydown-api/custom-buydown-api'
import { useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import { CustomBuydownPlan } from '@src/types/CustomBuydown'
import { useTranslation } from 'react-i18next'
import { PlanFormFields } from './components/PlanFormFields'
import { PlanSummaryCard } from './components/PlanSummaryCard'
import { CustomBuydownFormValues, customBuydownSchema } from './customBuydown-schema'

const defaultsFromPlan = (plan: CustomBuydownPlan | null): CustomBuydownFormValues => {
  if (!plan) return { contribution: 0.0499, activeFrom: DateOnly.today(), expiresOn: null, maxFinancingAmount: null }

  const max = plan.promoTermRates[0]?.maxFinancingAmount
  return {
    contribution: plan.merchantFeeRate,
    activeFrom: new DateOnly(new Date(plan.activeFrom)),
    expiresOn: plan.expiresOn ? new DateOnly(new Date(plan.expiresOn)) : null,
    maxFinancingAmount: max && max > 0 ? max : null,
  }
}

const CustomBuydownPage = () => {
  const { t } = useTranslation()
  const user = useUser()
  const merchantId = user?.merchantId ?? ''
  const [plan, isFetching] = useCurrentCustomBuydownPlan(merchantId)
  const [merchant] = useMerchantById(merchantId)
  const savePlan = useSaveCustomBuydownPlan(merchantId)
  const adminFeeRate = merchant?.lenderAdminFee ?? 0

  if (isFetching) return <PageSpinner isLoading />

  const formSetup = (): FormSetup<CustomBuydownFormValues> => ({
    resolver: yupResolver(customBuydownSchema),
    mode: 'onChange',
    defaultValues: defaultsFromPlan(plan),
  })

  const handleSubmit = (values: CustomBuydownFormValues) =>
    savePlan.mutateAsync({
      planKey: plan?.planKey,
      dto: {
        contribution: values.contribution,
        activeFrom: values.activeFrom.toString(),
        expiresOn: values.expiresOn?.toString() ?? undefined,
        maxFinancingAmount: values.maxFinancingAmount ?? undefined,
      },
    })

  return (
    <>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h5">{t('customBuydown.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('customBuydown.subtitle')}
        </Typography>
      </Stack>
      <FormContainerProvider<CustomBuydownFormValues, unknown, Promise<CustomBuydownPlan>>
        key={plan?.planKey ?? 'new'}
        useFormSetup={formSetup}
        onSubmit={handleSubmit}
        successMessage={(saved) => t('customBuydown.savedSuccess', { planCode: saved.label })}
      >
        <Grid container spacing={3} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 7 }}>
            <Card>
              <CardHeader avatar={<TuneIcon />} title={t('customBuydown.reduceRateTitle')} />
              <Divider />
              <CardContent>
                <FormBody<CustomBuydownFormValues>>
                  <PlanFormFields adminFeeRate={adminFeeRate} />
                </FormBody>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <PlanSummaryCard adminFeeRate={adminFeeRate} />
          </Grid>
        </Grid>
      </FormContainerProvider>
    </>
  )
}

export default CustomBuydownPage
